import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, dealsAPI, documentsAPI, inventoryAPI, uploadLinksAPI, paymentsAPI, lendersAPI, staffAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  ArrowLeft, User, Briefcase, FileText, DollarSign, Send, Download, Eye,
  Plus, Truck, FolderOpen, PenTool, Upload, Link as LinkIcon, Mail, Clock, Pencil,
  Search, Filter, Calendar, UserCheck, ShieldQuestion, HandCoins, Trash2, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  lead: 'Lead', qualified: 'Qualified', docs_pending: 'Docs Pending',
  submitted: 'Submitted', approved: 'Approved', funded: 'Funded',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};
const stageColors = {
  lead: 'bg-gray-100 text-gray-700 border border-gray-300', 
  qualified: 'bg-blue-100 text-blue-800 border border-blue-300',
  docs_pending: 'bg-amber-100 text-amber-800 border border-amber-300', 
  submitted: 'bg-purple-100 text-purple-800 border border-purple-300',
  approved: 'bg-green-100 text-green-800 border border-green-300', 
  funded: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  closed_won: 'bg-green-200 text-green-900 border border-green-400', 
  closed_lost: 'bg-red-100 text-red-800 border border-red-300',
};

export default function ClientPortfolio() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docDialog, setDocDialog] = useState(null);
  const [sendInvDialog, setSendInvDialog] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [dealForm, setDealForm] = useState({ inventory_ids: [], stage: 'lead', notes: '', amount: '', title: '', equipment_type: 'inventory', custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '', sale_condition: 'as_is' });
  const [docForm, setDocForm] = useState({ type: 'quote', deal_id: '', inventory_id: '', items: [{ description: '', amount: '' }], notes: '', subtotal: '', tax: '', total: '', deposit_amount: '' });
  const [lenders, setLenders] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitCreditOpen, setSubmitCreditOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({ inventory_id: '', broker_notes: '', amount: '', equipment_type: 'inventory', custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '' });
  const [submittingCredit, setSubmittingCredit] = useState(false);
  const [holdDepositDialog, setHoldDepositDialog] = useState(null);
  const [holdDepositAmount, setHoldDepositAmount] = useState('');
  const [sendingHoldDeposit, setSendingHoldDeposit] = useState(false);

  // Edit Client Info
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [clientForm, setClientForm] = useState({});
  const [savingClient, setSavingClient] = useState(false);

  // Edit Deal
  const [editDealOpen, setEditDealOpen] = useState(false);
  const [dealEditForm, setDealEditForm] = useState({});
  const [savingDeal, setSavingDeal] = useState(false);

  // Filtering
  const [dealFilter, setDealFilter] = useState('all');
  const [docSearch, setDocSearch] = useState('');
  const [docSourceFilter, setDocSourceFilter] = useState('all');
  const user = JSON.parse(localStorage.getItem('cts_user') || '{}');
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const [p, inv] = await Promise.all([clientsAPI.portfolio(clientId), inventoryAPI.list({ limit: 100 })]);
      setPortfolio(p.data);
      setInventory(inv.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);
  useEffect(() => { lendersAPI.list().then(r => setLenders(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) staffAPI.listForDropdown().then(r => setStaffList(r.data || [])).catch(() => {}); }, [isAdmin]);

  const client = portfolio?.client;
  const deals = portfolio?.deals || [];
  const documents = portfolio?.documents || [];
  const financing = portfolio?.financing || [];
  const activities = portfolio?.activities || [];

  // Filtered deals
  const filteredDeals = dealFilter === 'all' ? deals : deals.filter(d => d.stage === dealFilter);
  // Filtered documents
  const filteredDocs = documents.filter(d => {
    const matchesSearch = !docSearch || 
      d.type?.toLowerCase().includes(docSearch.toLowerCase()) ||
      d.filename?.toLowerCase().includes(docSearch.toLowerCase()) ||
      d.original_filename?.toLowerCase().includes(docSearch.toLowerCase()) ||
      d.deal_title?.toLowerCase().includes(docSearch.toLowerCase());
    const matchesSource = docSourceFilter === 'all' || d.source === docSourceFilter;
    return matchesSearch && matchesSource;
  });

  // Document generation
  const addDocItem = () => setDocForm(f => ({ ...f, items: [...f.items, { description: '', amount: '' }] }));
  const updateDocItem = (idx, key, val) => {
    setDocForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      const sub = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      const tax = sub * 0.13;
      return { ...f, items, subtotal: sub.toFixed(2), tax: tax.toFixed(2), total: (sub + tax).toFixed(2) };
    });
  };

  const handleGenerateDoc = async () => {
    setSaving(true);
    try {
      if (docForm.type === 'bill_of_sale') {
        // Use the new CTS BOS template endpoint
        const bosPayload = {
          ...docForm,
          client_id: clientId,
          inventory_id: docForm.inventory_id || '',
          total: parseFloat(docForm.total) || 0,
          doc_fee: parseFloat(docForm.doc_fee) || 0,
          licence_fee: parseFloat(docForm.licence_fee) || 0,
          trade_in_amount: parseFloat(docForm.trade_in_amount) || 0,
          deposit_amount: parseFloat(docForm.deposit_amount) || 0,
          payment_method: docForm.payment_method || 'Certified Funds',
          sale_condition: docForm.sale_condition || 'as_is',
          for_lender: docForm.for_lender || false,
          lender_id: docForm.lender_id || '',
          ship_to_same: docForm.for_lender ? false : docForm.ship_to_same !== false,
          ship_to_name: docForm.ship_to_name || '',
          ship_to_address: docForm.ship_to_address || '',
          ship_to_city: docForm.ship_to_city || '',
          ship_to_phone: docForm.ship_to_phone || '',
          notes: docForm.notes || '',
        };
        const res = await documentsAPI.generateBillOfSale(bosPayload);
        toast.success(`Bill of Sale ${res.data.invoice_number || ''} generated!`);
        if (res.data.url) window.open(`${API_URL}${res.data.url}`, '_blank');
        setDocDialog(null);
        load();
      } else {
        const payload = {
          ...docForm,
          client_id: clientId,
          items: docForm.items.map(i => ({ description: i.description, amount: parseFloat(i.amount) || 0 })),
          subtotal: parseFloat(docForm.subtotal) || undefined,
          tax: parseFloat(docForm.tax) || undefined,
          total: parseFloat(docForm.total) || undefined,
          deposit_amount: parseFloat(docForm.deposit_amount) || undefined,
        };
        const res = await documentsAPI.generate(payload);
        toast.success(`${docForm.type.replace(/_/g, ' ')} generated!`);
        if (res.data.url) window.open(`${API_URL}${res.data.url}`, '_blank');
        setDocDialog(null);
        load();
      }
    } catch { toast.error('Generation failed'); }
    setSaving(false);
  };

  const handleSendDoc = async (docId) => {
    try {
      await clientsAPI.sendDocument(clientId, docId);
      toast.success('Document sent to client!');
      load();
    } catch { toast.error('Send failed'); }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename || doc.original_filename || doc.type}"?`)) return;
    try {
      if (doc.source === 'staff_upload' && doc.deal_id) {
        await dealsAPI.deleteDealDoc(doc.deal_id, doc.id);
      } else if (doc.source === 'client_upload' && doc.deal_id) {
        await dealsAPI.deleteDealDoc(doc.deal_id, doc.id);
      } else {
        await documentsAPI.delete(doc.id);
      }
      toast.success('Document deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const handleViewDocument = async (doc) => {
    // Generated docs have a direct URL
    if (doc.url) {
      window.open(`${API_URL}${doc.url}`, '_blank');
      return;
    }
    // Staff uploads and client uploads need authenticated blob download
    if (doc.deal_id && doc.file_id) {
      try {
        const res = await dealsAPI.downloadDoc(doc.deal_id, doc.id);
        const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch { toast.error('Failed to load document'); }
      return;
    }
    toast.error('Document URL not available');
  };


  const handleSendPreApproval = async () => {
    if (!docForm.deal_id) { toast.error('Please select a deal'); return; }
    setSaving(true);
    try {
      const res = await uploadLinksAPI.sendPreApproval(clientId, docForm.deal_id, '');
      if (res.data?.email_sent) {
        toast.success('Pre-approval request sent to client!');
        setDocDialog(null);
        load();
      } else {
        toast.error(res.data?.email_error || 'Failed to send email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send pre-approval request');
    }
    setSaving(false);
  };

  const handleSendEsign = async (dealId) => {
    try {
      await clientsAPI.sendEsign(clientId, dealId);
      toast.success('E-sign credit application sent!');
      load();
    } catch { toast.error('Send failed'); }
  };

  const handleSubmitToCredit = async (e) => {
    e.preventDefault();
    setSubmittingCredit(true);
    try {
      const payload = {
        client_id: clientId,
        broker_notes: creditForm.broker_notes,
        amount: creditForm.amount ? parseFloat(creditForm.amount) : 0,
      };

      if (creditForm.equipment_type === 'inventory') {
        payload.inventory_id = creditForm.inventory_id === 'none' ? '' : creditForm.inventory_id;
      } else if (creditForm.equipment_type === 'tbd') {
        payload.custom_equipment = { make: 'TBD' };
      } else if (creditForm.equipment_type === 'private') {
        payload.custom_equipment = {
          make: creditForm.custom_make,
          model: creditForm.custom_model,
          mileage: creditForm.custom_mileage,
          price: creditForm.custom_price,
        };
        payload.seller_name = creditForm.seller_name;
      }

      await dealsAPI.submitToCredit(payload);
      toast.success('Submitted to credit! Redirecting to deals...');
      setSubmitCreditOpen(false);
      setCreditForm({ inventory_id: '', broker_notes: '', amount: '', equipment_type: 'inventory', custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '' });
      navigate('/admin/deals');
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    setSubmittingCredit(false);
  };

  const handleSendUploadLink = async (dealId) => {
    try {
      const res = await uploadLinksAPI.generate(clientId, dealId);
      const link = `${window.location.origin}/upload/${res.data.token}`;
      navigator.clipboard.writeText(link);
      toast.success('Upload link copied to clipboard! Send to client via email.');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleSendInventory = async () => {
    if (selectedUnits.length === 0) return;
    setSaving(true);
    try {
      await clientsAPI.sendInventory(clientId, selectedUnits);
      toast.success(`${selectedUnits.length} unit(s) sent!`);
      setSendInvDialog(false);
      setSelectedUnits([]);
      load();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleNewDeal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { client_id: clientId, stage: dealForm.stage, notes: dealForm.notes, title: dealForm.title, sale_condition: dealForm.sale_condition || 'as_is' };
      if (dealForm.amount) payload.amount = parseFloat(dealForm.amount);

      if (dealForm.equipment_type === 'inventory') {
        if (dealForm.inventory_ids.length > 0) {
          payload.inventory_ids = dealForm.inventory_ids;
          payload.inventory_id = dealForm.inventory_ids[0];
        }
      } else if (dealForm.equipment_type === 'tbd') {
        payload.equipment_tbd = true;
      } else if (dealForm.equipment_type === 'private') {
        payload.is_private_sale = true;
        payload.seller_name = dealForm.seller_name;
        payload.custom_equipment = {
          make: dealForm.custom_make,
          model: dealForm.custom_model,
          mileage: dealForm.custom_mileage,
          price: dealForm.custom_price,
        };
        if (!payload.amount && dealForm.custom_price) {
          payload.amount = parseFloat(dealForm.custom_price);
        }
      }

      await dealsAPI.create(payload);
      toast.success('Deal created');
      setNewDealOpen(false);
      setDealForm({ inventory_ids: [], stage: 'lead', notes: '', amount: '', title: '', equipment_type: 'inventory', custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '', sale_condition: 'as_is' });
      load();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleSendHoldDeposit = async () => {
    if (!holdDepositAmount || parseFloat(holdDepositAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSendingHoldDeposit(true);
    try {
      const res = await paymentsAPI.createHoldDepositLink({
        deal_id: holdDepositDialog.id,
        client_id: clientId,
        inventory_id: holdDepositDialog.inventory_id,
        amount: parseFloat(holdDepositAmount),
        description: `Hold Deposit - ${holdDepositDialog.unit?.title || 'Unit'}`,
      });
      toast.success(res.data.message);
      setHoldDepositDialog(null);
      setHoldDepositAmount('');
      load();
    } catch { toast.error('Failed to send hold deposit'); }
    setSendingHoldDeposit(false);
  };

  // Edit Client handlers
  const openEditClient = () => {
    setClientForm({
      name: client?.name || '', email: client?.email || '', phone: client?.phone || '',
      company: client?.company || '', salesperson: client?.salesperson || '',
      salesperson_id: client?.salesperson_id || '',
      preferred_makes: client?.preferred_makes || [], notes: client?.notes || '',
    });
    setEditClientOpen(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setSavingClient(true);
    try {
      await clientsAPI.update(clientId, clientForm);
      toast.success('Client info updated');
      setEditClientOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setSavingClient(false);
  };

  // Edit Deal handlers
  const openEditDeal = (deal) => {
    setDealEditForm({
      id: deal.id,
      inventory_id: deal.inventory_id || '',
      stage: deal.stage || 'lead',
      notes: deal.notes || '',
      amount: deal.amount?.toString() || '',
    });
    setEditDealOpen(true);
  };

  const handleSaveDeal = async (e) => {
    e.preventDefault();
    setSavingDeal(true);
    try {
      const payload = { ...dealEditForm };
      if (payload.amount) payload.amount = parseFloat(payload.amount);
      delete payload.id;
      await dealsAPI.update(dealEditForm.id, payload);
      toast.success('Deal updated');
      setEditDealOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update deal'); }
    setSavingDeal(false);
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-gray-100 rounded-lg" />)}</div>;
  if (!client) return <div className="text-gray-400 text-center py-20">Client not found</div>;

  return (
    <div data-testid="client-portfolio" className="w-full max-w-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')} className="text-gray-500" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4 mr-1" /> Clients
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-12 h-12 bg-[#E31937]/20 rounded-lg flex items-center justify-center flex-shrink-0"><User className="w-6 h-6 text-[#E31937]" /></div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
              <p className="text-gray-500 text-sm">{client.company || 'No company'} | {client.email} | {client.phone || 'No phone'}{client.salesperson ? ` | Sales: ${client.salesperson}` : ''}</p>
            </div>
            <Button variant="outline" size="sm" className="border-gray-200 text-gray-600" onClick={openEditClient} data-testid="edit-client-info">
              <Pencil className="w-3 h-3 mr-1" /> Edit Info
            </Button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap flex-shrink-0">
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C] text-xs" onClick={() => setSendInvDialog(true)} data-testid="send-inv-to-client"><Truck className="w-3 h-3 mr-1" /> Send Inventory</Button>
          <Button variant="outline" className="border-gray-300 text-gray-600 text-xs" onClick={() => setNewDealOpen(true)} data-testid="new-deal-btn">
            <Plus className="w-3 h-3 mr-1" /> New Deal
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Active Deals', value: deals.filter(d => !d.stage.startsWith('closed')).length, color: 'text-blue-400' },
          { label: 'Documents', value: documents.length, color: 'text-green-400' },
          { label: 'Financing Apps', value: financing.length, color: 'text-amber-400' },
          { label: 'Activities', value: activities.length, color: 'text-purple-400' },
          { label: 'Total Deals', value: deals.length, color: 'text-gray-600' },
        ].map(s => (
          <Card key={s.label} className="bg-white border-gray-200"><CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-gray-400 uppercase">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="deals" className="space-y-4">
        <TabsList className="bg-gray-100 border-gray-200">
          <TabsTrigger value="deals" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Deals & Folders</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">All Documents</TabsTrigger>
          <TabsTrigger value="financing" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Financing</TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Activity Log</TabsTrigger>
        </TabsList>

        {/* DEALS TAB - Grid View */}
        <TabsContent value="deals">
          {/* Filter bar */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="deals-filter-bar">
            <Select value={dealFilter} onValueChange={setDealFilter}>
              <SelectTrigger className="w-36 bg-white border-gray-200 text-xs h-8" data-testid="deal-filter-status">
                <Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {Object.entries(stageLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filteredDeals.length === 0 ? (
            <Card className="bg-white border-gray-200"><CardContent className="p-8 text-center text-gray-400">No deals {dealFilter !== 'all' ? 'matching this filter' : 'yet'}. Create one to start tracking.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="deals-grid">
              {filteredDeals.map(deal => (
                <Card
                  key={deal.id}
                  className="bg-white border-gray-200 hover:border-[#E31937]/40 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => navigate(`/admin/clients/${clientId}/deals/${deal.id}`)}
                  data-testid={`deal-card-${deal.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <FolderOpen className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#E31937] transition-colors">{deal.title || deal.unit?.title || 'General Deal'}</h3>
                          {deal.units && deal.units.length > 1 && (
                            <p className="text-[10px] text-gray-500">{deal.units.length} units: {deal.units.map(u => `${u.year} ${u.make} ${u.model}`).join(', ')}</p>
                          )}
                          {deal.units && deal.units.length === 1 && !deal.title && (
                            <p className="text-[10px] text-gray-500">{deal.units[0].year} {deal.units[0].make} {deal.units[0].model}</p>
                          )}
                          {deal.amount && <p className="text-xs text-[#E31937] font-bold">${deal.amount.toLocaleString()}</p>}
                          {deal.equipment_tbd && <span className="text-[10px] text-amber-600 font-medium">Equipment TBD</span>}
                          {deal.is_private_sale && <span className="text-[10px] text-indigo-600 font-medium">Private Sale</span>}
                        </div>
                      </div>
                      <Badge className={`text-[10px] px-2 py-0.5 flex-shrink-0 ${stageColors[deal.stage] || 'bg-gray-300/20 text-gray-600'}`}>
                        {stageLabels[deal.stage] || deal.stage}
                      </Badge>
                    </div>
                    <div className="space-y-1.5 text-[11px] text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        <span>Created {new Date(deal.created_at).toLocaleDateString()}</span>
                      </div>
                      {deal.created_by && (
                        <div className="flex items-center gap-1.5">
                          <UserCheck className="w-3 h-3" />
                          <span>{deal.created_by}</span>
                        </div>
                      )}
                      {deal.funding_date && (
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3" />
                          <span>Funding: {new Date(deal.funding_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
                      <span>{(deal.documents || []).length + (deal.deal_docs || []).length} docs</span>
                      <span>&middot;</span>
                      <span>{(deal.uploads || []).length} uploads</span>
                      {deal.hold_deposit_status && (
                        <>
                          <span>&middot;</span>
                          <span className={deal.hold_deposit_status === 'received' ? 'text-green-600' : 'text-amber-600'}>
                            Deposit {deal.hold_deposit_status}
                          </span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ALL DOCUMENTS TAB */}
        <TabsContent value="documents">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search by name, type, or deal..." value={docSearch} onChange={e => setDocSearch(e.target.value)} className="pl-9 bg-white border-gray-200 h-9 text-sm" data-testid="doc-search-input" />
            </div>
            <Select value={docSourceFilter} onValueChange={setDocSourceFilter}>
              <SelectTrigger className="w-full sm:w-[180px] h-9 bg-white border-gray-200 text-sm" data-testid="doc-source-filter">
                <Filter className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="generated">Generated Docs</SelectItem>
                <SelectItem value="staff_upload">Staff Uploads</SelectItem>
                <SelectItem value="client_upload">Client Uploads</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-gray-400 mb-3">{filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}</p>
          {filteredDocs.length === 0 ? (
            <Card className="bg-white border-gray-200"><CardContent className="p-8 text-center text-gray-400">{docSearch || docSourceFilter !== 'all' ? 'No documents matching filters' : 'No documents yet'}</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filteredDocs.map(doc => {
                const docName = doc.original_filename || doc.filename || doc.type?.replace(/_/g, ' ') || 'Document';
                const docUrl = doc.url || doc.file_id;
                const createdAt = doc.created_at ? new Date(doc.created_at) : null;
                const sourceLabel = doc.source === 'client_upload' ? 'Client Upload' : doc.source === 'staff_upload' ? 'Staff Upload' : 'Generated';
                const sourceBadge = doc.source === 'client_upload' ? 'bg-green-50 text-green-700 border-green-200' : doc.source === 'staff_upload' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200';
                return (
                  <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-gray-200 px-4 py-3 rounded-lg gap-2" data-testid={`doc-row-${doc.id}`}>
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{docName}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          <Badge className={`text-[10px] px-1.5 py-0 border ${sourceBadge}`}>{sourceLabel}</Badge>
                          {doc.deal_title && <span className="text-xs text-gray-500">{doc.deal_title}</span>}
                          {createdAt && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {createdAt.toLocaleDateString()} {createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-auto sm:ml-0">
                      {doc.total && <span className="text-[#E31937] font-bold text-sm mr-2">${doc.total.toLocaleString()}</span>}
                      {docUrl && (
                        <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc)} title="View document" data-testid={`doc-view-${doc.id}`}>
                          <Eye className="w-3.5 h-3.5 text-gray-500" />
                        </Button>
                      )}
                      {doc.source === 'generated' && (
                        <Button variant="ghost" size="sm" onClick={() => handleSendDoc(doc.id)} title="Send to client">
                          <Send className="w-3.5 h-3.5 text-blue-400" />
                        </Button>
                      )}
                      {user?.role === 'admin' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc)} title="Delete document" data-testid={`doc-delete-${doc.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* FINANCING TAB */}
        <TabsContent value="financing">
          {financing.length === 0 ? (
            <Card className="bg-white border-gray-200"><CardContent className="p-8 text-center text-gray-400">No financing applications</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {financing.map(app => (
                <Card key={app.id} className="bg-white border-gray-200 text-gray-900">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{app.desired_unit || 'General Application'}</p>
                        <p className="text-xs text-gray-400 mt-1">Credit: {app.credit_score_range || 'N/A'} | Revenue: {app.annual_revenue || 'N/A'}</p>
                        {app.down_payment && <p className="text-xs text-gray-500">Down payment: ${app.down_payment.toLocaleString()}</p>}
                      </div>
                      <div className="text-right">
                        <Badge className={
                          app.status === 'new' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                          app.status === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                          app.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-300' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-300' :
                          'bg-gray-100 text-gray-800 border border-gray-300'
                        }>{app.status}</Badge>
                        <p className="text-xs text-gray-400 mt-1">{new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ACTIVITY TAB */}
        <TabsContent value="activity">
          {activities.length === 0 ? (
            <Card className="bg-white border-gray-200"><CardContent className="p-8 text-center text-gray-400">No activity yet</CardContent></Card>
          ) : (
            <div className="space-y-1">
              {activities.map(a => (
                <div key={a.id} className="flex items-start gap-3 px-3 py-2 bg-white border border-gray-200 rounded text-xs text-gray-600">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${a.type === 'inventory_sent' ? 'bg-blue-500/20' : a.type === 'document_sent' ? 'bg-green-500/20' : a.type === 'esign_sent' ? 'bg-purple-500/20' : 'bg-gray-300/20'}`}>
                    {a.type === 'inventory_sent' ? <Truck className="w-3 h-3 text-blue-400" /> : a.type === 'document_sent' ? <FileText className="w-3 h-3 text-green-400" /> : a.type === 'esign_sent' ? <PenTool className="w-3 h-3 text-purple-400" /> : <Mail className="w-3 h-3 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <p>{a.message}</p>
                    <p className="text-gray-400 mt-0.5">{a.sent_by} | {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  <Badge variant="outline" className="text-[9px] border-gray-200">{a.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Document Dialog */}
      <Dialog open={docDialog === 'create'} onOpenChange={() => setDocDialog(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-white border-gray-200 text-gray-900" data-testid="create-doc-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 uppercase flex items-center gap-2"><FileText className="w-5 h-5 text-[#E31937]" /> Create Document for {client?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Type</label>
                <Select value={docForm.type} onValueChange={v => setDocForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="bg-gray-100 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Vehicle</label>
                <Select value={docForm.inventory_id} onValueChange={v => setDocForm(f => ({ ...f, inventory_id: v }))}>
                  <SelectTrigger className="bg-gray-100 border-gray-200 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{inventory.map(i => <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {(docForm.type === 'quote' || docForm.type === 'invoice') && (
              <div>
                <div className="flex justify-between mb-1"><label className="text-xs text-gray-500">Line Items</label><button className="text-xs text-[#E31937]" onClick={addDocItem}>+ Add</button></div>
                {docForm.items.map((it, idx) => (
                  <div key={idx} className="flex gap-2 mb-1.5">
                    <Input placeholder="Description" value={it.description} onChange={e => updateDocItem(idx, 'description', e.target.value)} className="bg-gray-100 border-gray-200 flex-1 text-xs" />
                    <Input placeholder="$" type="number" value={it.amount} onChange={e => updateDocItem(idx, 'amount', e.target.value)} className="bg-gray-100 border-gray-200 w-28 text-xs" />
                  </div>
                ))}
                <div className="text-right text-xs text-gray-500 mt-2">
                  Sub: ${docForm.subtotal || '0'} | HST: ${docForm.tax || '0'} | <span className="text-[#E31937] font-bold">Total: ${docForm.total || '0'}</span>
                </div>
              </div>
            )}
            {docForm.type === 'bill_of_sale' && (
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-800 mb-1">Professional CTS Bill of Sale</p>
                  <p className="text-xs text-red-600">Generates a branded, multi-page BOS with all details below.</p>
                </div>

                {/* Bill For: Client or Lender */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Bill For</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="billForCP" checked={!docForm.for_lender} onChange={() => setDocForm(f => ({ ...f, for_lender: false, lender_id: '' }))} className="accent-[#E31937]" data-testid="cp-bill-for-client" />
                      For Client
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="billForCP" checked={docForm.for_lender === true} onChange={() => setDocForm(f => ({ ...f, for_lender: true, ship_to_same: true }))} className="accent-[#E31937]" data-testid="cp-bill-for-lender" />
                      For Lender
                    </label>
                  </div>
                  {docForm.for_lender && (
                    <div>
                      <label className="text-xs text-gray-500">Select Lender</label>
                      <Select value={docForm.lender_id || ''} onValueChange={v => setDocForm(f => ({ ...f, lender_id: v }))}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 mt-1 text-xs" data-testid="cp-lender-select"><SelectValue placeholder="Choose a lender..." /></SelectTrigger>
                        <SelectContent>
                          {lenders.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-gray-400 mt-0.5">Buyer = Lender info, Ship To = Client info</p>
                    </div>
                  )}
                </div>

                {/* Condition of Sale */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Condition of Sale</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="saleConditionCP" checked={docForm.sale_condition !== 'safety_certified'} onChange={() => setDocForm(f => ({ ...f, sale_condition: 'as_is' }))} className="accent-[#E31937]" data-testid="cp-condition-as-is" />
                      Sold As-Is
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="saleConditionCP" checked={docForm.sale_condition === 'safety_certified'} onChange={() => setDocForm(f => ({ ...f, sale_condition: 'safety_certified' }))} className="accent-[#E31937]" data-testid="cp-condition-safety" />
                      Safety Certified
                    </label>
                  </div>
                </div>

                {/* Ship To (only show when NOT for lender) */}
                {!docForm.for_lender && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">Ship To</label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="shipToCP" checked={docForm.ship_to_same !== false} onChange={() => setDocForm(f => ({ ...f, ship_to_same: true }))} className="accent-[#E31937]" />
                      Same as Buyer
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="radio" name="shipToCP" checked={docForm.ship_to_same === false} onChange={() => setDocForm(f => ({ ...f, ship_to_same: false }))} className="accent-[#E31937]" />
                      Custom Address
                    </label>
                  </div>
                  {docForm.ship_to_same === false && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Company / Name" value={docForm.ship_to_name || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_name: e.target.value }))} className="bg-gray-100 border-gray-200 text-xs" />
                      <Input placeholder="Phone" value={docForm.ship_to_phone || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_phone: e.target.value }))} className="bg-gray-100 border-gray-200 text-xs" />
                      <Input placeholder="Address" value={docForm.ship_to_address || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_address: e.target.value }))} className="bg-gray-100 border-gray-200 text-xs" />
                      <Input placeholder="City, Province, Postal Code" value={docForm.ship_to_city || ''} onChange={e => setDocForm(f => ({ ...f, ship_to_city: e.target.value }))} className="bg-gray-100 border-gray-200 text-xs" />
                    </div>
                  )}
                </div>
                )}

                {/* Financial Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Sale Price ($)</label>
                    <Input type="number" value={docForm.total || ''} onChange={e => setDocForm(f => ({ ...f, total: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Document / Admin Fee ($)</label>
                    <Input type="number" value={docForm.doc_fee || ''} onChange={e => setDocForm(f => ({ ...f, doc_fee: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Licensing Fee ($)</label>
                    <Input type="number" value={docForm.licence_fee || ''} onChange={e => setDocForm(f => ({ ...f, licence_fee: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Trade-In Allowance ($)</label>
                    <Input type="number" value={docForm.trade_in_amount || ''} onChange={e => setDocForm(f => ({ ...f, trade_in_amount: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Hold Deposit ($)</label>
                    <Input type="number" value={docForm.deposit_amount || ''} onChange={e => setDocForm(f => ({ ...f, deposit_amount: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Payment Method</label>
                    <Select value={docForm.payment_method || 'Certified Funds'} onValueChange={v => setDocForm(f => ({ ...f, payment_method: v }))}>
                      <SelectTrigger className="bg-gray-100 border-gray-200 mt-1 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Certified Funds">Certified Funds</SelectItem>
                        <SelectItem value="Certified Cheque">Certified Cheque</SelectItem>
                        <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                        <SelectItem value="Bank Draft">Bank Draft</SelectItem>
                        <SelectItem value="Financing">Financing</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            {docForm.type === 'hold_deposit' && (
              <div><label className="text-xs text-gray-500">Deposit Amount</label>
                <Input type="number" value={docForm.deposit_amount} onChange={e => setDocForm(f => ({ ...f, deposit_amount: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" />
              </div>
            )}
            {docForm.type !== 'pre_approval' && (
              <Textarea placeholder="Notes" value={docForm.notes} onChange={e => setDocForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200" rows={2} />
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-gray-300" onClick={() => setDocDialog(null)}>Cancel</Button>
              <Button className="bg-[#E31937] text-white" onClick={handleGenerateDoc} disabled={saving}>{saving ? 'Generating...' : 'Generate & Download PDF'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Inventory Dialog */}
      <Dialog open={sendInvDialog} onOpenChange={setSendInvDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase"><Send className="w-5 h-5 inline mr-2 text-[#E31937]" />Send Inventory to {client?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {inventory.map(item => (
              <label key={item.id} className={`flex items-center gap-3 p-2 rounded border cursor-pointer ${selectedUnits.includes(item.id) ? 'border-[#E31937] bg-[#E31937]/10' : 'border-gray-200'}`}>
                <input type="checkbox" checked={selectedUnits.includes(item.id)} onChange={() => setSelectedUnits(p => p.includes(item.id) ? p.filter(x => x !== item.id) : [...p, item.id])} className="accent-[#E31937]" />
                <img src={item.images?.[0] || ''} alt="" className="w-14 h-10 object-cover rounded" />
                <div className="flex-1"><p className="text-sm">{item.title}</p><p className="text-xs text-gray-400">{item.stock_number}</p></div>
                <span className="text-[#E31937] font-bold text-sm">{item.price ? `$${item.price.toLocaleString()}` : 'Call'}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
            <Button variant="outline" className="border-gray-300" onClick={() => setSendInvDialog(false)}>Cancel</Button>
            <Button className="bg-[#E31937] text-white" onClick={handleSendInventory} disabled={saving || !selectedUnits.length}><Send className="w-4 h-4 mr-1" /> Send ({selectedUnits.length})</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Deal Dialog */}
      <Dialog open={newDealOpen} onOpenChange={setNewDealOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase flex items-center gap-2"><Plus className="w-5 h-5 text-[#E31937]" /> New Deal for {client?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleNewDeal} className="space-y-4">
            <div>
              <Label className="text-xs text-gray-500 uppercase mb-2 block">Deal Title</Label>
              <Input placeholder="Deal Title (optional)" value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} className="bg-gray-50 border-gray-200" data-testid="new-deal-title" />
            </div>

            {/* Equipment Type Selection */}
            <div>
              <Label className="text-xs text-gray-500 uppercase mb-2 block">Equipment</Label>
              <RadioGroup value={dealForm.equipment_type} onValueChange={v => setDealForm(f => ({ ...f, equipment_type: v, inventory_ids: [], custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '' }))} className="space-y-2" data-testid="equipment-type-selector">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${dealForm.equipment_type === 'inventory' ? 'border-[#E31937] bg-[#E31937]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="inventory" id="eq-inventory" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">From Inventory</span>
                    <p className="text-xs text-gray-400">Select a unit from your current inventory</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${dealForm.equipment_type === 'tbd' ? 'border-[#E31937] bg-[#E31937]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="tbd" id="eq-tbd" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Equipment TBD</span>
                    <p className="text-xs text-gray-400">Equipment to be determined later</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${dealForm.equipment_type === 'private' ? 'border-[#E31937] bg-[#E31937]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="private" id="eq-private" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Private Sale</span>
                    <p className="text-xs text-gray-400">Enter equipment &amp; seller details manually</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Conditional: Inventory Selector (Checkbox List) */}
            {dealForm.equipment_type === 'inventory' && (
              <div>
                <Label className="text-xs text-gray-500 uppercase mb-1 block">Select Equipment(s)</Label>
                {dealForm.inventory_ids.length > 0 && (
                  <p className="text-xs text-[#E31937] font-medium mb-2">{dealForm.inventory_ids.length} unit(s) selected</p>
                )}
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="equipment-checkbox-list">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search equipment..."
                        value={dealForm._equipSearch || ''}
                        onChange={e => setDealForm(f => ({ ...f, _equipSearch: e.target.value }))}
                        className="pl-8 bg-white border-gray-200 h-8 text-xs"
                        data-testid="equipment-search-input"
                      />
                    </div>
                  </div>
                  <div className="max-h-[240px] overflow-y-auto divide-y divide-gray-100">
                    {inventory
                      .filter(i => {
                        if (!dealForm._equipSearch) return true;
                        const s = dealForm._equipSearch.toLowerCase();
                        return `${i.year} ${i.make} ${i.model} ${i.stock_number} ${i.title}`.toLowerCase().includes(s);
                      })
                      .map(item => {
                        const isChecked = dealForm.inventory_ids.includes(item.id);
                        return (
                          <label
                            key={item.id}
                            className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isChecked ? 'bg-[#E31937]/5' : 'hover:bg-gray-50'}`}
                            data-testid={`equip-checkbox-${item.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setDealForm(f => ({
                                  ...f,
                                  inventory_ids: isChecked
                                    ? f.inventory_ids.filter(x => x !== item.id)
                                    : [...f.inventory_ids, item.id],
                                }));
                              }}
                              className="accent-[#E31937] w-4 h-4 rounded flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">{item.year} {item.make} {item.model}</p>
                              <p className="text-[10px] text-gray-400">Stock #{item.stock_number}{item.mileage_km ? ` · ${Number(item.mileage_km).toLocaleString()} km` : ''}</p>
                            </div>
                            <span className="text-xs font-semibold text-[#E31937] flex-shrink-0">{item.price ? `$${Number(item.price).toLocaleString()}` : '—'}</span>
                          </label>
                        );
                      })}
                    {inventory.filter(i => {
                      if (!dealForm._equipSearch) return true;
                      const s = dealForm._equipSearch.toLowerCase();
                      return `${i.year} ${i.make} ${i.model} ${i.stock_number} ${i.title}`.toLowerCase().includes(s);
                    }).length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-4">No equipment found</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Conditional: Private Sale Fields */}
            {dealForm.equipment_type === 'private' && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="private-sale-fields">
                <p className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1"><HandCoins className="w-3 h-3" /> Private Sale Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Make</Label>
                    <Input placeholder="e.g. Freightliner" value={dealForm.custom_make} onChange={e => setDealForm(f => ({ ...f, custom_make: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="private-sale-make" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Model</Label>
                    <Input placeholder="e.g. Cascadia" value={dealForm.custom_model} onChange={e => setDealForm(f => ({ ...f, custom_model: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="private-sale-model" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Mileage (KM)</Label>
                    <Input placeholder="e.g. 450000" type="number" value={dealForm.custom_mileage} onChange={e => setDealForm(f => ({ ...f, custom_mileage: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="private-sale-mileage" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Price ($)</Label>
                    <Input placeholder="e.g. 85000" type="number" value={dealForm.custom_price} onChange={e => setDealForm(f => ({ ...f, custom_price: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="private-sale-price" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Seller Name</Label>
                  <Input placeholder="Enter seller's name" value={dealForm.seller_name} onChange={e => setDealForm(f => ({ ...f, seller_name: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="private-sale-seller" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs text-gray-500 uppercase mb-1 block">Sale Condition</Label>
              <Select value={dealForm.sale_condition} onValueChange={v => setDealForm(f => ({ ...f, sale_condition: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="new-deal-sale-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="as_is">Sold As Is</SelectItem>
                  <SelectItem value="safety_certified">Safety Certified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 uppercase mb-1 block">Deal Amount ($)</Label>
              <Input placeholder="Deal Amount ($)" type="number" value={dealForm.amount} onChange={e => setDealForm(f => ({ ...f, amount: e.target.value }))} className="bg-gray-50 border-gray-200" data-testid="new-deal-amount" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 uppercase mb-1 block">Notes</Label>
              <Textarea placeholder="Notes" value={dealForm.notes} onChange={e => setDealForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-50 border-gray-200" rows={3} />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setNewDealOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={saving} data-testid="create-deal-submit">{saving ? 'Creating...' : 'Create Deal'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hold Deposit Dialog */}
      <Dialog open={!!holdDepositDialog} onOpenChange={() => setHoldDepositDialog(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="hold-deposit-dialog">
          <DialogHeader><DialogTitle className="text-gray-900">Send Hold Deposit Request</DialogTitle></DialogHeader>
          {holdDepositDialog && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <p className="font-semibold text-sm">{holdDepositDialog.unit?.title || 'Deal'}</p>
                <p className="text-xs text-gray-500">Client: {client?.name} ({client?.email})</p>
                {holdDepositDialog.amount && <p className="text-xs text-gray-500">Deal Amount: ${holdDepositDialog.amount.toLocaleString()}</p>}
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase block mb-1">Hold Deposit Amount ($)</label>
                <Input
                  type="number"
                  value={holdDepositAmount}
                  onChange={e => setHoldDepositAmount(e.target.value)}
                  placeholder="Enter amount..."
                  className="bg-gray-100 border-gray-200"
                  data-testid="hold-deposit-amount"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-semibold mb-1">Client will receive:</p>
                <p>1. Online payment link (Square) for ${holdDepositAmount || '0'}</p>
                <p>2. E-transfer details: <span className="font-mono">info@canadiantrucksales.ca</span></p>
              </div>

              <Button
                className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white"
                onClick={handleSendHoldDeposit}
                disabled={sendingHoldDeposit}
                data-testid="confirm-hold-deposit"
              >
                {sendingHoldDeposit ? 'Sending...' : `Send Hold Deposit ($${holdDepositAmount || '0'})`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Info Dialog */}
      <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle className="text-gray-900">Edit Client Information</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveClient} className="space-y-3">
            <div><label className="text-xs text-gray-500">Name</label><Input value={clientForm.name || ''} onChange={e => setClientForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" required data-testid="edit-client-name" /></div>
            <div><label className="text-xs text-gray-500">Email</label><Input type="email" value={clientForm.email || ''} onChange={e => setClientForm(f => ({ ...f, email: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" required /></div>
            <div><label className="text-xs text-gray-500">Phone</label><Input value={clientForm.phone || ''} onChange={e => setClientForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>
            <div><label className="text-xs text-gray-500">Company</label><Input value={clientForm.company || ''} onChange={e => setClientForm(f => ({ ...f, company: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>
            <div><label className="text-xs text-gray-500">Salesperson</label>
              {isAdmin ? (
                <Select value={clientForm.salesperson_id || '_none'} onValueChange={v => {
                  if (v === '_none') {
                    setClientForm(f => ({ ...f, salesperson: '', salesperson_id: '' }));
                  } else {
                    const s = staffList.find(st => st.id === v);
                    setClientForm(f => ({ ...f, salesperson: s?.name || '', salesperson_id: v }));
                  }
                }}>
                  <SelectTrigger className="bg-gray-100 border-gray-200 mt-1" data-testid="cp-salesperson-select"><SelectValue placeholder="Assign salesperson..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- None --</SelectItem>
                    {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={clientForm.salesperson || ''} disabled className="bg-gray-100 border-gray-200 mt-1" />
              )}
            </div>
            <div><label className="text-xs text-gray-500">Notes</label><Textarea value={clientForm.notes || ''} onChange={e => setClientForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={2} /></div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setEditClientOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={savingClient} data-testid="save-edit-client">{savingClient ? 'Saving...' : 'Update Client'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Deal Dialog */}
      <Dialog open={editDealOpen} onOpenChange={setEditDealOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="edit-deal-dialog">
          <DialogHeader><DialogTitle className="text-gray-900">Edit Deal</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveDeal} className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Stage</label>
              <Select value={dealEditForm.stage || 'lead'} onValueChange={v => setDealEditForm(f => ({ ...f, stage: v }))}>
                <SelectTrigger className="bg-gray-100 border-gray-200 mt-1" data-testid="edit-deal-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="deposit">Deposit</SelectItem>
                  <SelectItem value="financing">Financing</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Unit</label>
              <Select value={dealEditForm.inventory_id || 'none'} onValueChange={v => setDealEditForm(f => ({ ...f, inventory_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="bg-gray-100 border-gray-200 mt-1"><SelectValue placeholder="Select unit..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No Unit --</SelectItem>
                  {inventory.map(u => <SelectItem key={u.id} value={u.id}>{u.title} ({u.stock_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-gray-500">Amount ($)</label><Input type="number" value={dealEditForm.amount || ''} onChange={e => setDealEditForm(f => ({ ...f, amount: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="edit-deal-amount" /></div>
            <div><label className="text-xs text-gray-500">Notes</label><Textarea value={dealEditForm.notes || ''} onChange={e => setDealEditForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={2} /></div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setEditDealOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white hover:bg-[#C4122C]" disabled={savingDeal} data-testid="save-edit-deal">{savingDeal ? 'Saving...' : 'Update Deal'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Submit to Credit Dialog */}
      <Dialog open={submitCreditOpen} onOpenChange={setSubmitCreditOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase flex items-center gap-2"><DollarSign className="w-5 h-5 text-purple-600" /> Submit to Credit</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmitToCredit} className="space-y-4">
            <p className="text-sm text-gray-500">Submit <strong>{client?.name}</strong> to the credit pipeline.</p>

            {/* Equipment Type Selection */}
            <div>
              <Label className="text-xs text-gray-500 uppercase mb-2 block">Equipment</Label>
              <RadioGroup value={creditForm.equipment_type} onValueChange={v => setCreditForm(f => ({ ...f, equipment_type: v, inventory_id: '', custom_make: '', custom_model: '', custom_mileage: '', custom_price: '', seller_name: '' }))} className="space-y-2" data-testid="credit-equipment-type">
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${creditForm.equipment_type === 'inventory' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="inventory" id="credit-eq-inventory" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">From Inventory</span>
                    <p className="text-xs text-gray-400">Select a unit from your inventory</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${creditForm.equipment_type === 'tbd' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="tbd" id="credit-eq-tbd" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Equipment TBD</span>
                    <p className="text-xs text-gray-400">Equipment to be determined later</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${creditForm.equipment_type === 'private' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <RadioGroupItem value="private" id="credit-eq-private" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Private Sale</span>
                    <p className="text-xs text-gray-400">Enter equipment &amp; seller details manually</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Conditional: Inventory Selector */}
            {creditForm.equipment_type === 'inventory' && (
              <div>
                <Label className="text-xs text-gray-500">Equipment</Label>
                <Select value={creditForm.inventory_id} onValueChange={v => setCreditForm(f => ({ ...f, inventory_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="credit-inventory-select"><SelectValue placeholder="Select a unit..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific unit</SelectItem>
                    {inventory.filter(i => i.status === 'Available').map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.title || `${i.year} ${i.make} ${i.model}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Conditional: Private Sale Fields */}
            {creditForm.equipment_type === 'private' && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="credit-private-sale-fields">
                <p className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1"><HandCoins className="w-3 h-3" /> Private Sale Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Make</Label>
                    <Input placeholder="e.g. Freightliner" value={creditForm.custom_make} onChange={e => setCreditForm(f => ({ ...f, custom_make: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="credit-private-make" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Model</Label>
                    <Input placeholder="e.g. Cascadia" value={creditForm.custom_model} onChange={e => setCreditForm(f => ({ ...f, custom_model: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="credit-private-model" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Mileage (KM)</Label>
                    <Input placeholder="e.g. 450000" type="number" value={creditForm.custom_mileage} onChange={e => setCreditForm(f => ({ ...f, custom_mileage: e.target.value }))} className="bg-white border-gray-200 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Price ($)</Label>
                    <Input placeholder="e.g. 85000" type="number" value={creditForm.custom_price} onChange={e => setCreditForm(f => ({ ...f, custom_price: e.target.value }))} className="bg-white border-gray-200 mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Seller Name</Label>
                  <Input placeholder="Enter seller's name" value={creditForm.seller_name} onChange={e => setCreditForm(f => ({ ...f, seller_name: e.target.value }))} className="bg-white border-gray-200 mt-1" data-testid="credit-private-seller" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs text-gray-500">Amount</Label>
              <Input type="number" value={creditForm.amount} onChange={e => setCreditForm(f => ({ ...f, amount: e.target.value }))} placeholder="Deal amount" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Broker Notes (optional — AI can generate later)</Label>
              <Textarea value={creditForm.broker_notes} onChange={e => setCreditForm(f => ({ ...f, broker_notes: e.target.value }))} className="mt-1" rows={6} placeholder="Brief notes about the deal..." />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setSubmitCreditOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700" disabled={submittingCredit} data-testid="confirm-submit-credit">{submittingCredit ? 'Submitting...' : 'Submit to Credit'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
